import type { CSSProperties, ReactNode } from 'react';

import { cx } from '@/lib/cx';

import styles from './Chip.module.css';

export type ChipTone =
  | 'neutral'
  | 'applied'
  | 'details-mailed'
  | 'applicant-arranged'
  | 'applicant-done'
  | 'rsr-arranged'
  | 'approved'
  | 'onboarding'
  | 'hold'
  | 'rejected';

export type ChipProps = {
  children: ReactNode;
  /**
   * Which status this is.
   *
   * Resolved to tokens, never to a literal colour, because a status colour has to
   * mean the same thing in a chip, a chart bar, a board column and a tooltip dot
   * (plan 9.2).
   */
  tone?: ChipTone;
  size?: 'sm' | 'md';
  /** Adds a coloured dot. Text still carries the meaning; colour is never alone. */
  dot?: boolean;
  icon?: ReactNode;
  className?: string;
};

export function Chip({
  children,
  tone = 'neutral',
  size = 'md',
  dot = false,
  icon,
  className,
}: ChipProps) {
  const style = {
    '--chip-fg': `var(--status-${tone}-fg)`,
    '--chip-bg': `var(--status-${tone}-bg)`,
    color: 'var(--chip-fg)',
    background: 'var(--chip-bg)',
  } as CSSProperties;

  return (
    <span className={cx(styles.chip, size === 'sm' && styles.sm, className)} style={style}>
      {dot ? <span className={styles.dot} aria-hidden="true" /> : null}
      {icon}
      {children}
    </span>
  );
}
